<script setup lang="ts">
/**
 * /affiliate-programs/<slug> — the affiliate program pitched to one audience
 * (HR, recruiters, career coaches, AI tool sites…). Targets the searches of
 * people looking for a program to promote: "<audience> affiliate program",
 * "AI resume builder affiliate program", "affiliate program životopis". The
 * copy lives in constants/affiliatePrograms.ts; joining happens on /affiliate.
 */
import { AFFILIATE } from '~/constants/affiliate'
import { defaultLocaleSlug } from '~/constants/appLocale'
import { formatUsd } from '~/constants/pricing'
import { AFFILIATE_PROGRAM_PAGES, findAffiliateProgram, localizeAffiliateProgram } from '~/constants/affiliatePrograms'

const { t, locale } = useI18n()
const route = useRoute()
const { faqPageNode } = useJsonLd()

definePageMeta({
  layout: false,
  auth: false,
})

// Its URL in Czech, German and Polish (constants/localizedRoutes.ts).
false && /*#__PURE__*/ defineI18nRoute({ paths: { cs: '/affiliate-programy/[slug]', de: '/affiliate-programme/[slug]', pl: '/programy-afiliacyjne/[slug]' } })

// The English slug: under /cs and /de the URL carries a translated one.
const found = findAffiliateProgram(defaultLocaleSlug(route.path))
if (!found) {
  throw createError({ statusCode: 404, statusMessage: 'Page not found', fatal: true })
}

const page = computed(() => localizeAffiliateProgram(found, locale.value))
const path = `/affiliate-programs/${found.slug}`
const rate = `${Math.round(AFFILIATE.commissionRate * 100)} %`
const minPayout = formatUsd(AFFILIATE.minPayoutCents / 100)

const facts = computed(() => [
  { value: rate, label: t('affiliatePrograms.facts.rate') },
  { value: t('affiliatePrograms.facts.daysValue', { days: AFFILIATE.cookieDays }), label: t('affiliatePrograms.facts.cookie') },
  { value: t('affiliatePrograms.facts.daysValue', { days: AFFILIATE.holdDays }), label: t('affiliatePrograms.facts.hold') },
  { value: minPayout, label: t('affiliatePrograms.facts.min') },
])

const steps = computed(() => [
  t('affiliate.how.share'),
  t('affiliate.how.buy', { days: AFFILIATE.cookieDays }),
  t('affiliate.how.earn', { rate }),
  t('affiliate.how.payout', { hold: AFFILIATE.holdDays, min: minPayout }),
])

const others = computed(() => AFFILIATE_PROGRAM_PAGES
  .filter(other => other.slug !== found.slug)
  .map(other => ({ slug: other.slug, icon: other.icon, audience: localizeAffiliateProgram(other, locale.value).audience })))

const faqItems = computed(() => page.value.faq.map((item, i) => ({ id: String(i), question: item.q, answer: item.a })))
const extraNodes = computed(() => [faqPageNode(path, faqItems.value)])
</script>

<template>
  <MarketingFeaturePage
    :seo-title="page.seoTitle"
    :seo-description="page.seoDescription"
    :path="path"
    :breadcrumb-title="page.heading"
    :parent="{ name: t('affiliatePrograms.hub.heading'), path: '/affiliate-programs' }"
    :extra-nodes="extraNodes"
  >
    <div class="sofax">
      <section class="bg-white dark:bg-gray-950">
        <div class="section-space-sm">
          <div class="shell">
            <div class="mx-auto max-w-3xl rounded-[0.625rem] border-l-4 border-accent bg-surface p-[1.875rem] dark:bg-gray-800">
              <p class="text-lg">{{ page.short }}</p>
              <div class="mt-6 flex flex-wrap gap-3">
                <SofaxBtn :label="t('affiliatePrograms.cta.join')" to="/affiliate" />
                <SofaxBtn :label="t('affiliatePrograms.cta.terms')" variant="outline" to="/affiliate/terms" />
              </div>
            </div>

            <ul class="mx-auto mt-10 grid max-w-5xl grid-cols-2 gap-4 lg:grid-cols-4">
              <li v-for="fact in facts" :key="fact.label" class="rounded-[0.625rem] bg-surface p-6 text-center dark:bg-gray-800">
                <div class="font-title text-3xl font-bold text-accent">{{ fact.value }}</div>
                <div class="mt-2 text-sm text-muted dark:text-gray-400">{{ fact.label }}</div>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section class="bg-white dark:bg-gray-950">
        <div class="section-space-bottom">
          <div class="shell">
            <SofaxSectionHeading
              :eyebrow="t('affiliatePrograms.page.fitEyebrow')"
              :title="t('affiliatePrograms.page.fitTitle')"
              width="max-w-4xl"
            />
            <ul class="mx-auto grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-3">
              <li v-for="item in page.fit" :key="item.title" class="rounded-[0.625rem] border border-line p-[1.875rem] dark:border-gray-700">
                <h3 class="!text-xl">{{ item.title }}</h3>
                <p class="mt-3 text-base">{{ item.text }}</p>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section class="surface-alt">
        <div class="section-space">
          <div class="shell">
            <SofaxSectionHeading
              :eyebrow="t('affiliatePrograms.page.ideasEyebrow')"
              :title="t('affiliatePrograms.page.ideasTitle')"
              width="max-w-4xl"
            />
            <ul class="mx-auto grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-2">
              <li v-for="item in page.ideas" :key="item.title" class="rounded-[0.625rem] bg-white p-[1.875rem] dark:bg-gray-800">
                <h3 class="!text-xl">{{ item.title }}</h3>
                <p class="mt-3 text-base">{{ item.text }}</p>
              </li>
            </ul>
            <p class="mx-auto mt-8 max-w-5xl text-sm text-muted dark:text-gray-400">
              {{ t('affiliatePrograms.page.rulesNote') }}
              <LocaleLink to="/affiliate/terms" class="link">{{ t('affiliate.termsLink') }}</LocaleLink>
            </p>
          </div>
        </div>
      </section>

      <section class="bg-white dark:bg-gray-950">
        <div class="section-space">
          <div class="shell">
            <div class="mx-auto max-w-3xl">
              <h2>{{ t('affiliate.how.title') }}</h2>
              <ol class="mt-8 space-y-4">
                <li v-for="(line, index) in steps" :key="index" class="flex gap-4">
                  <span class="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent-soft/40 font-title font-bold text-ink dark:bg-accent/20 dark:text-accent-soft">{{ index + 1 }}</span>
                  <span class="pt-1">{{ line }}</span>
                </li>
              </ol>
              <div class="mt-8">
                <SofaxBtn :label="t('affiliatePrograms.cta.join')" to="/affiliate" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <MarketingFaqSection
      :title="t('affiliatePrograms.page.faqTitle')"
      :items="faqItems"
      :name="`affiliate-program-${found.slug}-faq`"
    />

    <section class="sofax bg-white dark:bg-gray-950">
      <div class="section-space-sm">
        <div class="shell">
          <h2 class="!text-3xl">{{ t('affiliatePrograms.page.othersTitle') }}</h2>
          <ul class="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <li v-for="other in others" :key="other.slug">
              <LocaleLink
                :to="`/affiliate-programs/${other.slug}`"
                class="flex h-full items-center gap-3 rounded-[0.625rem] border border-line p-5 transition-all duration-300 hover:shadow-lift dark:border-gray-700"
              >
                <i :class="['bi', other.icon, 'text-xl text-accent']" aria-hidden="true" />
                <span class="font-semibold text-ink dark:text-white">{{ other.audience }}</span>
              </LocaleLink>
            </li>
          </ul>
        </div>
      </div>
    </section>
  </MarketingFeaturePage>
</template>
